import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { StriimButton, StriimModal, StriimTypography } from "@striim/striim-ui";
import CircularProgress from "@mui/material/CircularProgress";

import { styles } from "../select-tables-modal/select-tables-modal.styles";
import ValidationList, {
    SetAPIStatusProperty,
    ValidationListElement,
    ValidationListStatus
} from "src/modules/wizards/common/validation-list/validation-list";
import { ValidationListRef } from "../select-tables-modal/components/validation-list/validation-list";
import { generateValidationList } from "./validation-steps/validation-steps";
import { ValidationReactView } from "app/components/appwizard/component-wizards/wizards/source/database-reader/connection-validation";
import api from "core/api/api";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import { CONNECTION_FAILURE_ERROR } from "../selectTables";

interface TestConnectionModalProps {
    dbType: string;
    adapterName: string;
    onVerifySuccess: () => void;
    onCancel: (hasConnectionFailed: boolean, message: string) => void;
    formValues: {
        [key: string]: any;
    };
    metaObjectId: string;
    isCDC: boolean;
}

const TestConnectionModal = (props: TestConnectionModalProps) => {
    const [apiStatus, setAPIStatus] = useState<SetAPIStatusProperty>({
        isAPISuccessful: false,
        isAPICompleted: false,
        canRetry: false
    });
    const [validationList, setValidationList] = useState<ValidationListElement[] | null>(null);

    const listAPIRef = useRef<ValidationListRef>();
    const timeoutRef = useRef<boolean>(true);
    const isCancelledRef = useRef<boolean>(false);

    useEffect(() => {
        if (validationList) return;

        let properties: { [key: string]: any } = {};
        const values = props.formValues;
        if (values) {
            for (let key in values) {
                if (Object.prototype.hasOwnProperty.call(values, key)) {
                    properties[key.toLowerCase()] = values[key];
                }
            }
        }

        const validationReactViewOptions = {
            isReact: true,

            type: props.dbType,
            isCDC: props.isCDC,
            adapterName: props.adapterName,

            connectionParams: {
                type: props.dbType,
                connectionUrl: properties.connectionurl,
                username: properties.username,
                encrypted: typeof properties.password === "object" ? true : false,
                objectID: props.metaObjectId,
                SSLConfig: properties.sslconfig,
                password:
                    typeof properties?.password === "object" ? properties.password?.encrypted : properties.password,
                authToken:
                    typeof properties.authtoken === "object" ? properties.authtoken?.encrypted : properties.authtoken,
                apiEndPoint: properties.apiendpoint,
                apiToken: properties.apitoken
                    ? typeof properties.apitoken === "object"
                        ? properties.apitoken.encrypted
                        : properties.apitoken
                    : properties.apitoken,
                host: properties.apiendpoint,
                clientId: properties.clientid,
                clientSecret: properties.clientsecret
                    ? properties.clientsecret
                    : typeof properties?.clientsecret === "object"
                    ? properties?.clientsecret?.encrypted
                    : properties?.clientsecret,
                refreshToken: properties.refreshtoken
                    ? properties.refreshtoken
                    : typeof properties?.refreshtoken === "object"
                    ? properties?.refreshtoken?.encrypted
                    : properties?.refreshtoken,
                authMode: properties?.authmode?.value,
                useConnectionProfile: properties.useconnectionprofile,
                connectionProfileName: properties.connectionprofilename,
                apiKey: properties.apikey,
                connectedAccount: properties.connectedaccount,
                clientCustomerId: properties.clientcustomerid,
                managerId: properties.managerid,
                isAutoTokenRenewal: properties.autotokenrenewal
            },

            appName: props.metaObjectId,
            templateID: props.metaObjectId
        };

        const validationReactView = new ValidationReactView(validationReactViewOptions);

        const generatedValidationList = generateValidationList({
            type: props.dbType,
            isCDC: props.isCDC,
            model: validationReactView.model
        });

        setValidationList([
            ...generatedValidationList,
            ...(generatedValidationList.length > 0
                ? [
                      {
                          status: ValidationListStatus.Todo,
                          text: "Minimal timeout", // In case if rest validation responses are too fast
                          hidden: true,
                          method: () =>
                              new Promise<true>((resolve, reject) => {
                                  setTimeout(() => (timeoutRef?.current ? resolve(true) : reject(false)), 2_000);
                              })
                      }
                  ]
                : [])
        ]);
    }, [validationList, props.formValues]);

    const handleVerifySuccess = () => {
        if (!isCancelledRef.current) props.onVerifySuccess();
    };

    const handleCancel = () => {
        isCancelledRef.current = true;
        timeoutRef.current = false;
        props.onCancel(apiStatus.canRetry, CONNECTION_FAILURE_ERROR);
    };

    return (
        <StriimModal
            isVisible
            onCancel={handleCancel}
            onConfirm={handleVerifySuccess}
            titleContent={<img src="app/images/test-db-connection.svg" alt="Test Connection" />}
            footerContent={
                <Grid container justifyContent={"flex-end"}>
                    <Grid item display={"flex"} gap={0.5}>
                        <StriimButton variant="text" onClick={handleCancel}>
                            Cancel
                        </StriimButton>

                        {apiStatus.canRetry ? (
                            <>
                                <StriimButton
                                    variant="primary"
                                    disabled={!apiStatus.isAPICompleted}
                                    onClick={() => {
                                        setValidationList(null);
                                        listAPIRef.current.retryAPI();
                                    }}
                                >
                                    Retry
                                </StriimButton>

                                {/*<StriimTooltip title="Skip validation (next steps may work incorrect)" placement="top">
                                    <StriimButton variant="error" onClick={props.onVerifySuccess}>
                                        Skip
                                    </StriimButton>
                                </StriimTooltip>*/}
                            </>
                        ) : null}
                    </Grid>
                </Grid>
            }
            size="large"
            autoHeight
            dialogProps={{
                onBackdropClick: handleCancel,
                sx: {
                    ".MuiDialog-paper": {
                        width: 641 // Same as title image + 1 border
                    },
                    ".MuiDialogTitle-root": {
                        padding: 0,
                        lineHeight: "0!important"
                    }
                }
            }}
            dialogActionProps={{
                sx: styles.dialogAction
            }}
            dialogTitleProps={{
                sx: styles.dialogTitle
            }}
            dialogContentProps={{
                sx: {
                    ...styles.dialogContent,
                    padding: ({ spacing }) => spacing(3) + "!important",
                    display: "flex",
                    flexDirection: "column",
                    gap: ({ spacing }) => spacing(1)
                }
            }}
        >
            <StriimTypography variant="h1" color="primary.700">
                Testing Connection to Database
            </StriimTypography>
            <StriimTypography variant="body4" color="greyscale.700">
                Before we can proceed to the next steps, we'll need to make sure Striim is able to access your database.
                Striim will fetch additional information about your database to help you in the next steps. Depending on
                the size of your database and the network latency, this may take a few minutes.
            </StriimTypography>

            {validationList ? (
                <ValidationList
                    onSuccess={handleVerifySuccess}
                    setAPIStatus={setAPIStatus}
                    ref={listAPIRef}
                    validationList={validationList}
                    controlAppWizardFooter={false}
                />
            ) : (
                <CircularProgress disableShrink color="secondary" size={20} thickness={8} />
            )}
        </StriimModal>
    );
};

export default TestConnectionModal;
